import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Modal, LinearProgress, TextField, CircularProgress, IconButton, InputLabel, Select, MenuItem, FormControl
} from '@mui/material';
import Icon from "./Icon";
import {
    Delete, Edit, Check
} from '@mui/icons-material';
import {CustomField, CustomFieldDefinitions, Presentation, Product, Slide} from '../services/types'

import SlideEditModal from '../components/SlideEditModal'
import {
    updatePresentation,
    uploadPhoto,
    deleteSlideFromPresentation, fetchProducts, fetchCustomFieldDef, addCustomField, updateCustomField
} from "../services/api";

interface ChapterSlide {
    id: string;
    slide: Slide;
}
interface Chapter {
    id: string;
    name: string;
    project_id: string;
    sort: number;
    external_id?: string;
    slides: ChapterSlide[];
}
interface PresentationEditorProps {
    presentation: Presentation;
    onUpload: (file: File) => Promise<void>;
    onDelete: () => Promise<void>;
    onUpdate: () => Promise<void>;
}
interface UploadAreaProps {
    onUpload: (file: File) => void;
}
const FIELD_TYPES = [
    { entity: 'SLIDE', title: 'Поля для слайдов' },
    { entity: 'PRESENTATION', title: 'Поля для презентаций' },
    { entity: 'VISIT', title: 'Поля для сценариев' }
];

type Field = {
    id: number;
    entity: 'SLIDE' | 'PRESENTATION' | 'SCENARIO';
    title: string;
    description: string;
    required: boolean;
};

let fieldId = 1;

const PresentationEditor: React.FC<PresentationEditorProps> = ({ presentation, onUpload, onDelete, onUpdate }) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [isSlideEditModalOpen, setIsSlideEditModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [currentPresenation, setCurrentPresenation] = useState<Presentation>();
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [deleteSlideId, setDeleteSlideId] = useState<number>(0);
    const [products, setProducts] = useState<Product[]>([]);
    const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CustomFieldDefinitions[]>([]);
    const [customFields, setCustomFields] = useState<CustomField[]>([]);
    const [dirtyIndexes, setDirtyIndexes] = useState<any>({});
    const [selectedSlideId, setSelectedSlideId] = useState<number>(0);
    const [fields, setFields] = useState<Record<string, Field[]>>({
        SLIDE: [],
        PRESENTATION: [],
        VISIT: []
    });


    const fetchProductsData = async () => {
        try {
            setLoading(true);
            const data = await fetchProducts(1);
            setProducts(data.items);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };
    const handleProductChange = (pid: number | string) => {
        products.map((product) => {
            if (pid == product.id) {
                setCurrentPresenation({
                    ...currentPresenation!!,
                    product: product
                });
            }
        })
    };

    const fetchCustomFieldDefinition = async () => {
        try {
            setLoading(true);
            const data = await fetchCustomFieldDef(1);
            const grouped: Record<string, Field[]> = { SLIDE: [], PRESENTATION: [], VISIT: [] };
            for (const item of data.items) {
                if (grouped[item.entity]) {
                    grouped[item.entity].push({
                        id: item.id,
                        entity: item.entity,
                        title: item.title,
                        description: item.description,
                        required: item.required
                    });
                }
            }
            setFields(grouped);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };
    useEffect(() => {
        setLoading(false)
        setUpdating(false)
        setCurrentPresenation(presentation)
        setCustomFields(presentation.customFields)
        fetchProductsData();
        fetchCustomFieldDefinition();
    }, [presentation.id]);


    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            onUpload(file);
        }
    };


    const handleSlideClick = (slide: Slide) => {
    };

    const handleSave = async () => {
        try {
            const updateData = {
                title: currentPresenation?.title,
                description: currentPresenation?.description,
                pictureId: currentPresenation?.picture.id!!,
                productId: currentPresenation?.product.id!!
            }
            await updatePresentation(currentPresenation?.id, updateData);
        } catch (error) {
            console.error('Error inviting team member:', error)
        }
    };


    const handleUpload = async (file: File) => {
        setIsUploadModalOpen(true);
        try {
            const response = await uploadPhoto(file, (progress) => {
                setUploadProgress(progress);
            });
            if (currentPresenation != null){
                setCurrentPresenation({
                    ...currentPresenation,
                    picture: response
                });
            }
        } catch (error) {
            console.error('Error uploading video:', error);
        } finally {
            setIsUploadModalOpen(false);
            setUploadProgress(0);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file && file.type === 'video/mp4') {
            handleUpload(file);
        }
    };
    const handleDeletePrompt = (id: number) => {
        setDeleteSlideId(id)
        setIsDeleteConfirmOpen(true);
    };
    const handleDeleteConfirm = async () => {
        try {
            await deleteSlideFromPresentation(deleteSlideId);
            setIsDeleteConfirmOpen(false);
            onDelete()
        } catch (error) {
            console.error('Error deleting product:', error);
        }
    };
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleUpload(file);
        }
    };
    const UploadArea: React.FC<UploadAreaProps> = ({ onUpload }) => (
        <Box
            sx={{
                border: '1px dashed grey',
                borderRadius: 1,
                p: 0,
                mt: 2,
                mb: 1,
                textAlign: 'center',
                cursor: 'pointer',
                overflow: 'hidden',
                fontSize: 0
            }}
            onClick={() => document.getElementById('photo-upload')?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
        >
            {
                currentPresenation?.picture.id !== 1 ?
                    <img src={'https://stage.selfdetailing.ru'+currentPresenation?.picture.raw} alt={currentPresenation?.picture.raw} style={{maxHeight: '256px', objectFit:'contain', width:'100%'}}/>
                    :
                    <Typography sx={{lineHeight: '80px', fontSize: '16px'}}>Нажмите или перетащите фото</Typography>
            }
            <input
                id="photo-upload"
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
        </Box>
    );

    const handleEditSlide = async (visitSlideId: number) => {
        setIsSlideEditModalOpen(true)
        setSelectedSlideId(visitSlideId)
    };
    const handleEditSlideClose = async () => {
        setIsSlideEditModalOpen(false)
        setSelectedSlideId(0)
    };

    const handleInputChange = (index: number, value: string) => {
        setCustomFields(prevFields => {
            const newFields = [...prevFields];
            newFields[index] = { ...newFields[index], value };
            return newFields;
        });
        setDirtyIndexes((prev: any) => ({ ...prev, [index]: true }));
    };
    const handleSaveCustomField = async (id: number, value: string, index: number) => {
        await updateCustomField(id, {value: value})
        setDirtyIndexes((prev: any) => ({ ...prev, [index]: false }));
    };

    const handleAddCustomField = async (definitionId: any) => {
        try {
            console.log(definitionId)
            const addData = {
                value: "",
                definitionId: definitionId,
                presentationId: currentPresenation?.id
            }
            await addCustomField(addData);
            onUpdate()
        } catch (error) {
            console.error("Ошибка при добавлении поля:", error);
        }
    };


    return (
        <Box display="flex" sx={{height: 'calc(100vh - 64px)'}}>
            {!loading && (
                <>
                    <Box sx={{display: 'flex', flexDirection: 'column', width: '384px', height: '100%', background: '#3a485b36', boxSizing: 'border-box', overflowY: 'scroll'}}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                            accept=".zip"
                        />
                        <Button sx={{color: '#3a485b', display: 'flex', gap: '4px', height: '50px', borderBottom: '#fff3 solid 1px'}} onClick={handleUploadClick}><Icon name="upload"/>&nbsp;Загрузить архив</Button>
                        <Box sx={{padding: '10px 20px'}}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Название"
                                value={currentPresenation?.title}
                                onChange={(e) => {
                                    setCurrentPresenation({
                                        ...currentPresenation!!,
                                        title: e.target.value
                                    });
                                }}
                                multiline
                                rows={1}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Описание"
                                value={currentPresenation?.description}
                                onChange={(e) => {
                                    setCurrentPresenation({
                                        ...currentPresenation!!,
                                        description: e.target.value
                                    });
                                }}
                                multiline
                                rows={4}
                            />
                            <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
                                <InputLabel id="project-select-label">Выберите продукт</InputLabel>
                                <Select
                                    labelId="project-select-label"
                                    id="project-select"
                                    value={currentPresenation?.product.id}
                                    label="Выберите проект"
                                    onChange={(e) => handleProductChange(e.target.value)}
                                >
                                    {products.map((product) => (
                                        <MenuItem key={product.id} value={product.id}>
                                            {product.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <UploadArea onUpload={handleUpload} />
                            <Button onClick={handleSave} variant="contained" sx={{mt: 2, background:'#3a485b', width: '100%'}}>
                                Сохранить
                            </Button>

                            <Box sx={{mt: 2}}>
                                <Typography variant="subtitle1" sx={{mb: 1, mt: 4}}><b>Дополнительные параметры презентации:</b></Typography>
                                {customFields.map((field, index) => (
                                    <Box key={field.definition.id} sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            label={field.definition.title}
                                            value={field.value}
                                            onChange={(e) => handleInputChange(index, e.target.value)}
                                            multiline
                                            rows={1}
                                        />
                                        {dirtyIndexes[index] && (
                                            <Button onClick={() => handleSaveCustomField(field.id, field.value, index)} variant="contained" sx={{ml: 1, position: 'absolute', right: '8px', top: '26px', width: '20px', minWidth: '40px'}}>
                                                Ok
                                            </Button>
                                        )}
                                    </Box>
                                ))}
                                {fields.PRESENTATION.map((field) => {
                                        const exists = currentPresenation?.customFields?.some(
                                            (cf) => cf.definition.id === field.id
                                        );
                                        return (
                                            <Box key={field.id} sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                                                {!exists && (
                                                    <>
                                                        <Typography sx={{flex: 1}}>{field.title}</Typography>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => handleAddCustomField(field.id)}
                                                        >
                                                            Добавить
                                                        </Button>
                                                    </>

                                                )}
                                            </Box>
                                        );
                                    })
                                }

                            </Box>
                        </Box>
                    </Box>

                    <Box className={!isSlideEditModalOpen ? "presentationRightContainer" : "presentationRightContainerNoScroll"}>
                        {!isSlideEditModalOpen ?
                            <Box sx={{width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'flex-start', gap: '10px', p: 2}}>
                                {currentPresenation?.slides.map((slide) => (
                                    <Box className='slideItem' sx={{width: '240px', '&:hover .deleteButton': {opacity: 1}, '&:hover .editButton': {opacity: 1}}} key={slide.id} onClick={() => handleSlideClick(slide)}>
                                        {slide.picture ?
                                            <Box>
                                                <img src={'https://stage.selfdetailing.ru'+slide.picture.raw} alt={slide.picture.raw} style={{maxHeight: '256px', objectFit:'cover', width:'100%'}}/>
                                            </Box>
                                            : null}
                                        <Box className='slideItemText'>
                                            {slide.title}
                                        </Box>
                                        <IconButton
                                            className="editButton"
                                            sx={{
                                                position: 'absolute',
                                                top: 5,
                                                right: 55,
                                                opacity: 0,
                                                transition: 'opacity 0.2s',
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                '&:hover': {backgroundColor: 'rgba(255, 255, 255, 0.9)'}
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onMouseUp={(e) => {e.stopPropagation(); handleEditSlide(slide.id);}}
                                        >
                                            <Edit/>
                                        </IconButton>
                                        <IconButton
                                            className="deleteButton"
                                            sx={{
                                                position: 'absolute',
                                                top: 5,
                                                right: 5,
                                                opacity: 0,
                                                transition: 'opacity 0.2s',
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                '&:hover': {backgroundColor: 'rgba(255, 255, 255, 0.9)'}
                                            }}
                                            onClick={(e) => {e.stopPropagation(); handleDeletePrompt(slide.id)}}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                            :
                            <></>
                        }
                    </Box>
                </>
            )}

            {loading && (
                <Box className='notFoundElements'>
                    <CircularProgress/>
                </Box>
            )}

            {updating && (
                <Box className='notFoundElements' sx={{backgroundColor: 'rgba(0, 0, 0, 0.6)'}}>
                    <Box sx={{p:3, backgroundColor:'white', borderRadius: '10px'}}>
                        <CircularProgress/>
                    </Box>
                </Box>
            )}

            <Modal open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
                <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                    <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 400, p: 3 }}>
                        <Typography variant="h6" sx={{mb: 2}}>
                            Вы действительно хотите удалить слайд?
                        </Typography>
                        <Box sx={{display:'flex', justifyContent: 'flex-end', gap: 2}}>
                            <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Удалить</Button>
                            <Button variant="outlined" onClick={() => setIsDeleteConfirmOpen(false)}>Отмена</Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Модальные окна */}
            <Modal open={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)}>
                <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                    <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 600 }}>
                        <Box sx={{ /* стили */ }}>
                            <Typography sx={{mb: 3}}>Пожалуйста, дождитесь завершения загрузки</Typography>
                            <LinearProgress variant="determinate" value={uploadProgress} />
                            <Typography>{uploadProgress}%</Typography>
                        </Box>
                    </Box>
                </Box>
            </Modal>


            <Modal open={isSlideEditModalOpen}>
                <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                    <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, padding: 0, width: 'calc(100vw - 180px)', height: 'calc(100vh - 180px)', overflow: 'hidden' }}>
                        <SlideEditModal slideId={selectedSlideId} onClose={handleEditSlideClose}></SlideEditModal>
                    </Box>
                </Box>
            </Modal>


        </Box>
    );
};
export default PresentationEditor;
